import { Box } from '@mui/material'
import React from 'react'
import Txt from './Txt'
import { Company } from '../utils/company'
import Card from './Card'

// Spiegelt api/src/utils/stripeOnboarding.ts (Rechtsform-Listen, Handelsregister-Format,
// Namens-Split, Adress-Aufbau) — bitte synchron halten.
const SOLE_PROPRIETOR_LEGAL_FORMS = new Set([
	'einzelunternehmen',
	'einzelunternehmer',
	'einzelkaufmann',
	'einzelkauffrau',
	'einzelkaufmännin',
	'e.k.',
	'eingetragener kaufmann',
	'eingetragene kauffrau',
	'freiberufler',
	'freiberuflerin',
	'freelancer',
])

const COMPANY_LEGAL_FORMS = new Set([
	'gmbh',
	'gmbh & co. kg',
	'gmbh & co kg',
	'ug',
	'ug (haftungsbeschränkt)',
	'ug haftungsbeschränkt',
	'unternehmergesellschaft',
	'ag',
	'aktiengesellschaft',
	'kg',
	'kommanditgesellschaft',
	'ohg',
	'offene handelsgesellschaft',
	'gbr',
	'gesellschaft bürgerlichen rechts',
	'partg',
	'partg mbb',
	'partnerschaftsgesellschaft',
	'se',
	'societas europaea',
	'ev',
	'e.v.',
	'eingetragener verein',
	'eg',
	'e.g.',
	'eingetragene genossenschaft',
])

const HANDELSREGISTER_PATTERN = /^[A-Z]{2,3} \d{2,10}( [A-Z]{1,2})?$/

const sxRow = {
	display: 'grid',
	gridTemplateColumns: '260px 1fr',
	gap: '0.75rem',
	mb: '0.4rem',
} as const

const sxLabelMargin = { m: 0 } as const

type PrefillRow = { label: string; value?: string | null; note?: string }

const Row = React.memo(
	({ label, value, note }: PrefillRow): JSX.Element => (
		<Box sx={sxRow}>
			<Txt variant="body2" color="text.secondary" sx={sxLabelMargin}>
				{label}
			</Txt>
			<Box>
				<Txt variant="body2" color={value ? 'text.primary' : 'text.disabled'} sx={sxLabelMargin}>
					{value || 'wird nicht gesendet'}
				</Txt>
				{note ? (
					<Txt variant="caption" color="error.main" sx={sxLabelMargin}>
						{note}
					</Txt>
				) : null}
			</Box>
		</Box>
	)
)
Row.displayName = 'Row'

const StripePrefill = ({ company }: { company?: Company | null }): JSX.Element => {
	const legalFormKey = company?.legalForm?.trim().toLowerCase() ?? ''
	const businessType = SOLE_PROPRIETOR_LEGAL_FORMS.has(legalFormKey)
		? 'individual'
		: COMPANY_LEGAL_FORMS.has(legalFormKey)
		? 'company'
		: null

	const rows = React.useMemo<PrefillRow[]>(() => {
		if (!businessType) return []

		const line1 =
			company?.street && company?.houseNumber
				? `${company.street} ${company.houseNumber}`
				: company?.street ?? null
		const address =
			company?.zipCode || company?.city || company?.street
				? [line1, [company?.zipCode, company?.city].filter(Boolean).join(' '), 'DE']
						.filter(Boolean)
						.join(', ')
				: null

		const nameParts = company?.managingDirector?.trim().split(/\s+/) ?? []
		const firstName = nameParts.length > 1 ? nameParts[0] : null
		const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : nameParts[0] ?? null

		const normalizedTradeRegister = company?.tradeRegisterNumber
			? company.tradeRegisterNumber.trim().toUpperCase().replace(/\s+/g, ' ')
			: null
		const tradeRegisterValid =
			normalizedTradeRegister !== null && HANDELSREGISTER_PATTERN.test(normalizedTradeRegister)

		const common: PrefillRow[] = [
			{ label: 'type', value: 'express' },
			{ label: 'country', value: 'DE' },
			{ label: 'email', value: company?.email },
			{ label: 'business_type', value: businessType },
			{ label: 'business_profile.name', value: company?.name },
			{ label: 'business_profile.mcc', value: '5812 (Restaurants)' },
			{ label: 'business_profile.url', value: company?.website },
		]

		return businessType === 'individual'
			? [
					...common,
					{ label: 'individual.first_name', value: firstName },
					{ label: 'individual.last_name', value: lastName },
					{ label: 'individual.email', value: company?.email },
					{ label: 'individual.phone', value: company?.phone },
					{ label: 'individual.address', value: address },
			  ]
			: [
					...common,
					{ label: 'company.name', value: company?.name },
					{ label: 'company.phone', value: company?.phone },
					{ label: 'company.address', value: address },
					{
						label: 'company.tax_id (Handelsregister)',
						value: tradeRegisterValid ? normalizedTradeRegister : null,
						note:
							normalizedTradeRegister && !tradeRegisterValid
								? `„${company?.tradeRegisterNumber}" passt nicht auf das Format (z. B. „HRB 12345") und wird verworfen.`
								: undefined,
					},
					{ label: 'company.vat_id (USt-IdNr.)', value: company?.ustId },
			  ]
	}, [businessType, company])

	return (
		<Card mb="1.5rem">
			<Txt variant="h4" fontWeight="bold" m="0" color="primary.main">
				Daten für Stripe
			</Txt>
			<Txt variant="body2" color="text.secondary" m="0.5rem 0 1rem 0">
				Genau diese Felder werden beim Anlegen des Connect-Accounts übertragen. Alles Übrige fragt
				Stripe im Onboarding selbst ab.
			</Txt>
			{businessType ? (
				rows.map((row) => (
					<Row key={row.label} label={row.label} value={row.value} note={row.note} />
				))
			) : (
				<Txt variant="body2" color="error.main">
					{company?.legalForm
						? `Rechtsform „${company.legalForm}" ist keiner Stripe-Kategorie zugeordnet — das Onboarding bricht ab, bevor ein Account entsteht.`
						: 'Ohne Rechtsform bricht das Onboarding ab, bevor ein Account entsteht.'}
				</Txt>
			)}
		</Card>
	)
}

export default React.memo(StripePrefill)
